import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Search, 
  Download, 
  Loader2, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Package,
  Filter,
  ScanBarcode,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  History,
  Trash2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import BarcodeScanner from '@/components/BarcodeScanner';
import SkuOrderHistoryDialog from '@/components/SkuOrderHistoryDialog';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

function InventoryStockList() {
  var loadingState = React.useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var stockListState = React.useState([]);
  var stockList = stockListState[0];
  var setStockList = stockListState[1];
  
  var totalState = React.useState(0);
  var total = totalState[0];
  var setTotal = totalState[1];
  
  var pageState = React.useState(1);
  var page = pageState[0];
  var setPage = pageState[1];
  
  var totalPagesState = React.useState(1);
  var totalPages = totalPagesState[0];
  var setTotalPages = totalPagesState[1];
  
  var searchState = React.useState('');
  var searchTerm = searchState[0];
  var setSearchTerm = searchState[1];
  
  var statusFilterState = React.useState('');
  var statusFilter = statusFilterState[0];
  var setStatusFilter = statusFilterState[1];
  
  var brandFilterState = React.useState('');
  var brandFilter = brandFilterState[0];
  var setBrandFilter = brandFilterState[1];
  
  var categoryFilterState = React.useState('');
  var categoryFilter = categoryFilterState[0];
  var setCategoryFilter = categoryFilterState[1];

  var historyProductState = React.useState(null);
  var historyProduct = historyProductState[0];
  var setHistoryProduct = historyProductState[1];

  var historyOpenState = React.useState(false);
  var historyOpen = historyOpenState[0];
  var setHistoryOpen = historyOpenState[1];

  // Per "Unified Product Location Logic": filter Stock Master by Rack / Bin so
  // users can pull "all SKUs in Rack A" with a single click. Both filters are
  // sent only when non-empty and the backend matches partial + case-insensitive.
  var rackFilterState = React.useState('');
  var rackFilter = rackFilterState[0];
  var setRackFilter = rackFilterState[1];

  var binFilterState = React.useState('');
  var binFilter = binFilterState[0];
  var setBinFilter = binFilterState[1];
  
  var exportingState = React.useState(false);
  var exporting = exportingState[0];
  var setExporting = exportingState[1];
  
  var detailDialogState = React.useState(false);
  var detailDialogOpen = detailDialogState[0];
  var setDetailDialogOpen = detailDialogState[1];
  
  var selectedProductState = React.useState(null);
  var selectedProduct = selectedProductState[0];
  var setSelectedProduct = selectedProductState[1];
  
  var productDetailState = React.useState(null);
  var productDetail = productDetailState[0];
  var setProductDetail = productDetailState[1];

  var scannerModeState = React.useState(false);
  var scannerMode = scannerModeState[0];
  var setScannerMode = scannerModeState[1];

  // Selection state
  var selectedItemsState = React.useState([]);
  var selectedItems = selectedItemsState[0];
  var setSelectedItems = selectedItemsState[1];

  function toggleStockSelection(id) {
    setSelectedItems(function(prev) { return prev.indexOf(id) >= 0 ? prev.filter(function(x) { return x !== id; }) : prev.concat(id); });
  }
  function toggleStockSelectAll() {
    setSelectedItems(selectedItems.length === stockList.length ? [] : stockList.map(function(s) { return s.id; }));
  }
  function handleDeleteSelectedStock() {
    if (selectedItems.length === 0) return;
    if (!window.confirm('Delete ' + selectedItems.length + ' item(s) from Stock Master? Stock ledger entries will also be removed.')) return;
    var token = localStorage.getItem('token');
    axios.post(API + '/inventory/stock/bulk-delete', { ids: selectedItems }, { headers: { Authorization: 'Bearer ' + token } })
      .then(function() { toast.success(selectedItems.length + ' items deleted'); setSelectedItems([]); fetchStockList(); })
      .catch(function(err) { toast.error(err.response && err.response.data ? err.response.data.detail : 'Delete failed'); });
  }
  function handleDeleteSingleStock(id) {
    if (!window.confirm('Delete this item from Stock Master?')) return;
    var token = localStorage.getItem('token');
    axios.delete(API + '/products/' + id, { headers: { Authorization: 'Bearer ' + token } })
      .then(function() { toast.success('Item deleted'); fetchStockList(); })
      .catch(function(err) { toast.error(err.response && err.response.data ? err.response.data.detail : 'Delete failed'); });
  }

  // Bulk Upload States
  var bulkDialogState = React.useState(false);
  var bulkDialogOpen = bulkDialogState[0];
  var setBulkDialogOpen = bulkDialogState[1];
  
  var bulkStepState = React.useState(1); // 1: Upload, 2: Preview, 3: Confirm, 4: Result
  var bulkStep = bulkStepState[0];
  var setBulkStep = bulkStepState[1];
  
  var bulkFileState = React.useState(null);
  var bulkFile = bulkFileState[0];
  var setBulkFile = bulkFileState[1];
  
  var validatingState = React.useState(false);
  var validating = validatingState[0];
  var setValidating = validatingState[1];
  
  var validRowsState = React.useState([]);
  var validRows = validRowsState[0];
  var setValidRows = validRowsState[1];
  
  var errorRowsState = React.useState([]);
  var errorRows = errorRowsState[0];
  var setErrorRows = errorRowsState[1];
  
  var updateModeState = React.useState('add'); // 'add' or 'replace'
  var updateMode = updateModeState[0];
  var setUpdateMode = updateModeState[1];
  
  var adjustReasonState = React.useState('');
  var adjustmentReason = adjustReasonState[0];
  var setAdjustmentReason = adjustReasonState[1];
  
  var submittingBulkState = React.useState(false);
  var submittingBulk = submittingBulkState[0];
  var setSubmittingBulk = submittingBulkState[1];
  
  var bulkResultState = React.useState(null);
  var bulkResult = bulkResultState[0];
  var setBulkResult = bulkResultState[1];
  
  var historyDialogState = React.useState(false);
  var historyDialogOpen = historyDialogState[0];
  var setHistoryDialogOpen = historyDialogState[1];
  
  var uploadHistoryState = React.useState([]);
  var uploadHistory = uploadHistoryState[0];
  var setUploadHistory = uploadHistoryState[1];
  
  var userRoleState = React.useState('staff');
  var userRole = userRoleState[0];
  var setUserRole = userRoleState[1];

  // Stock-drift detector — quietly polls the backend on mount to surface a
  // banner when products.current_stock is out of sync with sum(batches).
  // Admins see a one-click "Reconcile" link; everyone else sees an info note.
  var driftState = React.useState({ total: 0, orphan: 0, phantom: 0 });
  var drift = driftState[0];
  var setDrift = driftState[1];

  React.useEffect(function() {
    var token = localStorage.getItem('token');
    axios.get(API + '/inventory/stock-drift', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) {
        var d = res.data || {};
        setDrift({
          total: d.total_drifted || 0,
          orphan: d.orphan_stock_products || 0,
          phantom: d.phantom_batch_products || 0,
        });
      })
      .catch(function() { /* silently ignore — banner just won't show */ });
  }, []);

  React.useEffect(function() {
    // Get user role from localStorage (run once on mount)
    try {
      var storedUser = localStorage.getItem('user');
      if (storedUser) {
        var user = JSON.parse(storedUser);
        setUserRole(user.role || 'staff');
      }
    } catch { /* Falls back to default role. */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ref keeps the LATEST searchTerm visible inside the memoized fetchStockList.
  // We deliberately keep `searchTerm` out of the useCallback deps to avoid a
  // backend hit on every keystroke — but the previous implementation captured
  // an empty string in the closure, so manual Apply Filters / Enter never
  // reached the backend. Reading from a ref fixes that without changing UX.
  var searchTermRef = React.useRef(searchTerm);
  React.useEffect(function() { searchTermRef.current = searchTerm; }, [searchTerm]);

  var fetchStockList = React.useCallback(function() {
    setLoading(true);
    var token = localStorage.getItem('token');
    var payload = {
      page: page,
      page_size: 30,
      search: searchTermRef.current || undefined,
      status: statusFilter || undefined,
      brand: brandFilter || undefined,
      category: categoryFilter || undefined,
      rack: rackFilter || undefined,
      bin: binFilter || undefined
    };
    
    axios.post(API + '/inventory/stock-list', payload, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setStockList(response.data.items || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
      setLoading(false);
    }).catch(function(error) {
      toast.error('Failed to fetch stock list');
      setLoading(false);
    });
    // searchTerm read via ref above — manual trigger only, no fetch on every keystroke.
  }, [page, statusFilter, brandFilter, categoryFilter, rackFilter, binFilter]);

  React.useEffect(function() {
    fetchStockList();
  }, [fetchStockList]);

  function handleSearch() {
    if (page !== 1) {
      // setPage triggers re-render → useCallback recomputes → useEffect fires fetch
      setPage(1);
    } else {
      // Already on page 1 — setPage is a no-op, so trigger fetch directly.
      // searchTermRef now carries the latest value typed by the user.
      fetchStockList();
    }
  }

  function handleBarcodeProductFound(product) {
    // When barcode scanned, show product details immediately
    viewProductDetail(product);
  }

  function viewProductDetail(product) {
    setSelectedProduct(product);
    var token = localStorage.getItem('token');
    axios.get(API + '/inventory/product/' + product.id, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setProductDetail(response.data);
      setDetailDialogOpen(true);
    }).catch(function(error) {
      toast.error('Failed to fetch product details');
    });
  }

  function exportInventory() {
    setExporting(true);
    var token = localStorage.getItem('token');
    var payload = {
      search: searchTerm || undefined,
      brand: brandFilter || undefined,
      category: categoryFilter || undefined,
      rack: rackFilter || undefined,
      bin: binFilter || undefined,
      low_stock_only: statusFilter === 'low_stock'
    };
    
    axios.post(API + '/export/inventory', payload, {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob'
    }).then(function(response) {
      var url = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Inventory_Stock_' + new Date().toISOString().slice(0,10) + '.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Inventory exported');
      setExporting(false);
    }).catch(function(error) {
      toast.error('Failed to export');
      setExporting(false);
    });
  }

  function getStatusBadge(status) {
    if (status === 'out_of_stock') {
      return React.createElement(Badge, { className: 'bg-red-100 text-red-800' }, 'Out of Stock');
    } else if (status === 'low_stock') {
      return React.createElement(Badge, { className: 'bg-amber-100 text-amber-800' }, 'Low Stock');
    }
    return React.createElement(Badge, { className: 'bg-green-100 text-green-800' }, 'In Stock');
  }

  function formatCurrency(val) {
    return '₹' + (val || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }

  // Bulk Upload Functions
  function downloadTemplate() {
    var token = localStorage.getItem('token');
    axios.get(API + '/inventory/bulk-upload/template', {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob'
    }).then(function(response) {
      var url = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inventory_bulk_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    }).catch(function(error) {
      toast.error('Failed to download template');
    });
  }

  function handleFileSelect(e) {
    var file = e.target.files[0];
    if (file) {
      if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
        toast.error('Please upload an Excel or CSV file (.xlsx, .xls or .csv)');
        return;
      }
      setBulkFile(file);
    }
  }

  function validateUpload() {
    if (!bulkFile) {
      toast.error('Please select a file first');
      return;
    }
    
    setValidating(true);
    var token = localStorage.getItem('token');
    var formData = new FormData();
    formData.append('file', bulkFile);
    
    axios.post(API + '/inventory/bulk-upload/validate', formData, {
      headers: { 
        Authorization: 'Bearer ' + token,
        'Content-Type': 'multipart/form-data'
      }
    }).then(function(response) {
      setValidRows(response.data.valid_rows || []);
      setErrorRows(response.data.error_rows || []);
      setValidating(false);
      setBulkStep(2);
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Failed to validate file');
      setValidating(false);
    });
  }

  function executeBulkUpload() {
    if (!adjustmentReason.trim()) {
      toast.error('Please provide an adjustment reason');
      return;
    }
    
    setSubmittingBulk(true);
    var token = localStorage.getItem('token');
    
    var payload = {
      updates: validRows,
      update_mode: updateMode,
      adjustment_reason: adjustmentReason
    };
    
    axios.post(API + '/inventory/bulk-upload/execute', payload, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setBulkResult(response.data);
      setSubmittingBulk(false);
      setBulkStep(4);
      // Refresh stock list
      fetchStockList();
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Failed to execute bulk upload');
      setSubmittingBulk(false);
    });
  }

  function resetBulkUpload() {
    setBulkStep(1);
    setBulkFile(null);
    setValidRows([]);
    setErrorRows([]);
    setUpdateMode('add');
    setAdjustmentReason('');
    setBulkResult(null);
  }

  function closeBulkDialog() {
    setBulkDialogOpen(false);
    resetBulkUpload();
  }

  function fetchUploadHistory() {
    var token = localStorage.getItem('token');
    axios.get(API + '/inventory/bulk-upload/history', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setUploadHistory(response.data.uploads || []);
    }).catch(function(error) {
      toast.error('Failed to fetch upload history');
    });
  }

  function openHistoryDialog() {
    fetchUploadHistory();
    setHistoryDialogOpen(true);
  }

  function downloadErrorReport(errors) {
    if (!errors || errors.length === 0) {
      toast.error('No errors to download');
      return;
    }
    
    var token = localStorage.getItem('token');
    axios.post(API + '/inventory/bulk-upload/error-report', errors, {
      headers: { 
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json'
      },
      responseType: 'blob'
    }).then(function(response) {
      var url = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inventory_upload_errors.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Error report downloaded');
    }).catch(function(error) {
      toast.error('Failed to download error report');
    });
  }

  function downloadHistoryErrorReport(uploadId) {
    var token = localStorage.getItem('token');
    axios.get(API + '/inventory/bulk-upload/history/' + uploadId + '/error-report', {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob'
    }).then(function(response) {
      var url = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'upload_errors_' + uploadId.slice(0, 8) + '.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Error report downloaded');
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Failed to download error report');
    });
  }

  var canBulkUpload = userRole === 'admin' || userRole === 'warehouse_manager' || userRole === 'manager';

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'inventory-stock-list' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'text-3xl font-heading font-bold text-primary' }, 'Stock Master'),
        React.createElement('p', { className: 'text-slate-600 mt-1' }, 'View and manage inventory stock levels')
      ),
      React.createElement('div', { className: 'flex gap-2 flex-wrap' },
        canBulkUpload && React.createElement(Button, { 
          variant: 'default',
          onClick: function() { setBulkDialogOpen(true); }, 
          className: 'gap-2 bg-purple-600 hover:bg-purple-700',
          'data-testid': 'bulk-upload-btn'
        },
          React.createElement(Upload, { className: 'w-4 h-4' }),
          'Bulk Upload Inventory'
        ),
        canBulkUpload && React.createElement(Button, { 
          variant: 'outline',
          onClick: openHistoryDialog, 
          className: 'gap-2',
          'data-testid': 'upload-history-btn'
        },
          React.createElement(History, { className: 'w-4 h-4' }),
          'Upload History'
        ),
        React.createElement(Button, { 
          variant: scannerMode ? 'default' : 'outline', 
          onClick: function() { setScannerMode(!scannerMode); }, 
          className: 'gap-2 ' + (scannerMode ? 'bg-green-600 hover:bg-green-700' : ''),
          'data-testid': 'toggle-scanner-btn'
        },
          React.createElement(ScanBarcode, { className: 'w-4 h-4' }),
          scannerMode ? 'Scanner ON' : 'Scanner Mode'
        ),
        React.createElement(Button, { variant: 'outline', onClick: exportInventory, disabled: exporting, className: 'gap-2' },
          exporting ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Download, { className: 'w-4 h-4' }),
          'Export to Excel'
        ),
        React.createElement(CsvDownloadButton, { onDownload: exportInventory, testId: 'inventory-export-csv', size: 'default', disabled: exporting }),
        React.createElement(Button, { variant: 'outline', onClick: fetchStockList, className: 'gap-2' },
          React.createElement(RefreshCw, { className: 'w-4 h-4' }),
          'Refresh'
        ),
        selectedItems.length > 0 && React.createElement(Button, { variant: 'outline', onClick: handleDeleteSelectedStock, className: 'gap-2 border-red-300 text-red-600 hover:bg-red-50', 'data-testid': 'delete-selected-stock' },
          React.createElement(Trash2, { className: 'w-4 h-4' }),
          'Delete Selected (' + selectedItems.length + ')'
        )
      )
    ),

    // Barcode Scanner Section (when enabled)
    scannerMode && React.createElement(Card, { className: 'bg-green-50 border-green-200' },
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex items-center gap-2 mb-3' },
          React.createElement(ScanBarcode, { className: 'w-5 h-5 text-green-600' }),
          React.createElement('h3', { className: 'font-semibold text-green-800' }, 'Barcode Scanner Mode'),
          React.createElement(Badge, { className: 'bg-green-600' }, 'Active')
        ),
        React.createElement(BarcodeScanner, {
          onProductFound: handleBarcodeProductFound,
          placeholder: 'Scan barcode with USB scanner...',
          autoFocus: true,
          showProductPreview: true
        })
      )
    ),

    // Filters
    React.createElement(Card, null,
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex flex-wrap gap-4 items-end' },
          // Search
          React.createElement('div', { className: 'flex-1 min-w-[200px]' },
            React.createElement('label', { className: 'text-sm font-medium text-slate-600 mb-1 block' }, 'Search'),
            React.createElement('div', { className: 'relative' },
              React.createElement(Search, { className: 'absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4' }),
              React.createElement(Input, {
                placeholder: 'Search SKU, name, barcode...',
                value: searchTerm,
                onChange: function(e) { setSearchTerm(e.target.value); },
                onKeyDown: function(e) { if (e.key === 'Enter') handleSearch(); },
                className: 'pl-9'
              })
            )
          ),
          
          // Status Filter
          React.createElement('div', { className: 'w-40' },
            React.createElement('label', { className: 'text-sm font-medium text-slate-600 mb-1 block' }, 'Status'),
            React.createElement('select', {
              value: statusFilter,
              onChange: function(e) { setStatusFilter(e.target.value); setPage(1); },
              className: 'w-full h-10 px-3 border rounded-md text-sm'
            },
              React.createElement('option', { value: '' }, 'All Status'),
              React.createElement('option', { value: 'in_stock' }, 'In Stock'),
              React.createElement('option', { value: 'low_stock' }, 'Low Stock'),
              React.createElement('option', { value: 'out_of_stock' }, 'Out of Stock')
            )
          ),
          
          // Brand Filter
          React.createElement('div', { className: 'w-40' },
            React.createElement('label', { className: 'text-sm font-medium text-slate-600 mb-1 block' }, 'Brand'),
            React.createElement(Input, {
              placeholder: 'Filter by brand',
              value: brandFilter,
              onChange: function(e) { setBrandFilter(e.target.value); }
            })
          ),
          
          // Category Filter
          React.createElement('div', { className: 'w-40' },
            React.createElement('label', { className: 'text-sm font-medium text-slate-600 mb-1 block' }, 'Category'),
            React.createElement(Input, {
              placeholder: 'Filter by category',
              value: categoryFilter,
              onChange: function(e) { setCategoryFilter(e.target.value); }
            })
          ),

          // Rack Filter (Unified Product Location Logic)
          React.createElement('div', { className: 'w-32' },
            React.createElement('label', { className: 'text-sm font-medium text-slate-600 mb-1 block' }, 'Rack'),
            React.createElement(Input, {
              placeholder: 'e.g. A1',
              value: rackFilter,
              onChange: function(e) { setRackFilter(e.target.value); },
              'data-testid': 'stock-rack-filter'
            })
          ),

          // Bin Filter
          React.createElement('div', { className: 'w-32' },
            React.createElement('label', { className: 'text-sm font-medium text-slate-600 mb-1 block' }, 'Bin'),
            React.createElement(Input, {
              placeholder: 'e.g. B-07',
              value: binFilter,
              onChange: function(e) { setBinFilter(e.target.value); },
              'data-testid': 'stock-bin-filter'
            })
          ),

          React.createElement(Button, { onClick: handleSearch, className: 'gap-2' },
            React.createElement(Filter, { className: 'w-4 h-4' }),
            'Apply Filters'
          )
        )
      )
    ),

    // Results Summary
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('p', { className: 'text-sm text-slate-600' }, 
        'Showing ', stockList.length, ' of ', total, ' products'
      ),
      React.createElement('div', { className: 'flex items-center gap-2' },
        React.createElement(Button, { 
          variant: 'outline', 
          size: 'sm', 
          disabled: page <= 1,
          onClick: function() { setPage(page - 1); }
        }, React.createElement(ChevronLeft, { className: 'w-4 h-4' })),
        React.createElement('span', { className: 'text-sm' }, 'Page ', page, ' of ', totalPages),
        React.createElement(Button, { 
          variant: 'outline', 
          size: 'sm', 
          disabled: page >= totalPages,
          onClick: function() { setPage(page + 1); }
        }, React.createElement(ChevronRight, { className: 'w-4 h-4' }))
      )
    ),

    // Stock Table
    React.createElement(Card, null,
      React.createElement(CardContent, { className: 'p-0' },
        loading 
          ? React.createElement('div', { className: 'flex items-center justify-center py-12' },
              React.createElement(Loader2, { className: 'w-8 h-8 animate-spin text-blue-600' })
            )
          : React.createElement('div', { className: 'overflow-x-auto' },
              React.createElement('table', { className: 'w-full text-sm' },
                React.createElement('thead', null,
                  React.createElement('tr', { className: 'border-b bg-slate-50' },
                    React.createElement('th', { className: 'p-3 w-10' },
                      React.createElement('input', { type: 'checkbox', checked: selectedItems.length === stockList.length && stockList.length > 0, onChange: toggleStockSelectAll, className: 'w-4 h-4 accent-blue-600' })),
                    React.createElement('th', { className: 'text-left p-3 font-semibold' }, 'Product ID'),
                    React.createElement('th', { className: 'text-left p-3 font-semibold' }, 'Product Name'),
                    React.createElement('th', { className: 'text-left p-3 font-semibold' }, 'Barcode'),
                    React.createElement('th', { className: 'text-left p-3 font-semibold' }, 'Brand / Category'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Current Stock'),
                    React.createElement('th', { className: 'text-left p-3 font-semibold' }, 'Rack'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Purchase Rate'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Selling Price'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Margin %'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'MRP'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Stock Value'),
                    React.createElement('th', { className: 'text-center p-3 font-semibold' }, 'Status'),
                    React.createElement('th', { className: 'text-center p-3 font-semibold' }, 'Actions')
                  )
                ),
                React.createElement('tbody', null,
                  stockList.map(function(item) {
                    var status = item.stock_status || item.status || 'in_stock';
                    var isSelected = selectedItems.indexOf(item.id) >= 0;
                    return React.createElement('tr', { 
                      key: item.id, 
                      className: 'border-b hover:bg-slate-50 ' + (isSelected ? 'bg-blue-50 ' : '') + (status === 'out_of_stock' ? 'bg-red-50' : status === 'low_stock' ? 'bg-amber-50' : '')
                    },
                      React.createElement('td', { className: 'p-3 text-center' },
                        React.createElement('input', { type: 'checkbox', checked: isSelected, onChange: function() { toggleStockSelection(item.id); }, className: 'w-4 h-4 accent-blue-600' })),
                      React.createElement('td', { className: 'p-3 font-mono text-xs' }, item.product_id),
                      React.createElement('td', { className: 'p-3 font-medium' }, item.name),
                      React.createElement('td', { className: 'p-3 font-mono text-xs text-slate-700' }, item.barcode || '-'),
                      React.createElement('td', { className: 'p-3' },
                        React.createElement('p', { className: 'font-medium' }, item.brand || '-'),
                        React.createElement('p', { className: 'text-xs text-slate-500' }, item.category || '-')
                      ),
                      React.createElement('td', { className: 'p-3 text-right font-semibold ' + (status === 'out_of_stock' ? 'text-red-600' : '') }, 
                        item.current_stock, ' ', item.measurement_unit || item.unit || 'PCS'
                      ),
                      React.createElement('td', { className: 'p-3 text-slate-700' },
                        item.rack
                          ? React.createElement('span', { className: 'inline-block px-2 py-0.5 rounded bg-slate-100 text-xs font-mono' }, item.rack + (item.bin ? ' / ' + item.bin : ''))
                          : React.createElement('span', { className: 'text-slate-400' }, '-')
                      ),
                      React.createElement('td', { className: 'p-3 text-right' }, formatCurrency(item.b2b_price || item.last_purchase_rate)),
                      React.createElement('td', { className: 'p-3 text-right' }, formatCurrency(item.outward_price || 0)),
                      React.createElement('td', { className: 'p-3 text-right font-medium' },
                        item.margin_pct === null || item.margin_pct === undefined
                          ? React.createElement('span', { className: 'text-slate-400' }, 'N/A')
                          : React.createElement('span', { className: item.margin_pct >= 0 ? 'text-emerald-600' : 'text-red-600' }, item.margin_pct + '%')
                      ),
                      React.createElement('td', { className: 'p-3 text-right' }, formatCurrency(item.mrp)),
                      React.createElement('td', { className: 'p-3 text-right font-semibold' }, formatCurrency(item.stock_value)),
                      React.createElement('td', { className: 'p-3 text-center' }, getStatusBadge(status)),
                      React.createElement('td', { className: 'p-3 text-center' },
                        React.createElement('div', { className: 'flex justify-center gap-1' },
                          React.createElement(Button, {
                            variant: 'ghost',
                            size: 'sm',
                            title: 'Order History',
                            onClick: function() { setHistoryProduct(item); setHistoryOpen(true); },
                            className: 'text-blue-600 hover:text-blue-800 hover:bg-blue-50',
                            'data-testid': 'sku-history-btn-' + item.product_id
                          }, React.createElement(History, { className: 'w-4 h-4' })),
                          React.createElement(Button, { 
                            variant: 'ghost', 
                            size: 'sm',
                            onClick: function() { viewProductDetail(item); }
                          }, React.createElement(Eye, { className: 'w-4 h-4' })),
                          React.createElement(Button, { 
                            variant: 'ghost', 
                            size: 'sm',
                            onClick: function() { handleDeleteSingleStock(item.id); },
                            className: 'text-red-500 hover:text-red-700 hover:bg-red-50'
                          }, React.createElement(Trash2, { className: 'w-4 h-4' }))
                        )
                      )
                    );
                  })
                )
              )
            )
      )
    ),

    // Product Detail Dialog
    React.createElement(Dialog, { open: detailDialogOpen, onOpenChange: setDetailDialogOpen },
      React.createElement(DialogContent, { className: 'max-w-2xl' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
            React.createElement(Package, { className: 'w-5 h-5' }),
            'Product Stock Details'
          )
        ),
        productDetail && React.createElement('div', { className: 'space-y-4' },
          // Product Info
          React.createElement('div', { className: 'grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg' },
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-slate-600' }, 'Product Name'),
              React.createElement('p', { className: 'font-semibold' }, productDetail.product?.name)
            ),
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-slate-600' }, 'SKU'),
              React.createElement('p', { className: 'font-mono' }, productDetail.product?.product_id)
            ),
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-slate-600' }, 'Brand / Category'),
              React.createElement('p', null, productDetail.product?.brand, ' / ', productDetail.product?.category)
            ),
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-slate-600' }, 'Location'),
              React.createElement('p', null, productDetail.product?.rack_location || 'Not assigned')
            )
          ),
          
          // Stock Info
          React.createElement('div', { className: 'grid grid-cols-3 gap-4' },
            React.createElement(Card, { className: 'p-4 text-center' },
              React.createElement('p', { className: 'text-sm text-slate-600' }, 'Current Stock'),
              React.createElement('p', { className: 'text-2xl font-bold' }, productDetail.stock_info?.current_stock),
              React.createElement('p', { className: 'text-xs' }, productDetail.product?.unit)
            ),
            React.createElement(Card, { className: 'p-4 text-center' },
              React.createElement('p', { className: 'text-sm text-slate-600' }, 'FIFO Value'),
              React.createElement('p', { className: 'text-2xl font-bold text-green-600' }, formatCurrency(productDetail.stock_info?.fifo_value))
            ),
            React.createElement(Card, { className: 'p-4 text-center' },
              React.createElement('p', { className: 'text-sm text-slate-600' }, 'Avg Cost (FIFO)'),
              React.createElement('p', { className: 'text-2xl font-bold' }, formatCurrency(productDetail.stock_info?.avg_cost))
            )
          ),
          
          // Pricing
          React.createElement('div', { className: 'grid grid-cols-4 gap-4 text-sm' },
            React.createElement('div', { className: 'p-3 bg-blue-50 rounded' },
              React.createElement('p', { className: 'text-blue-600' }, 'B2B Price'),
              React.createElement('p', { className: 'font-semibold' }, formatCurrency(productDetail.product?.b2b_price))
            ),
            React.createElement('div', { className: 'p-3 bg-green-50 rounded' },
              React.createElement('p', { className: 'text-green-600' }, 'Selling Price'),
              React.createElement('p', { className: 'font-semibold' }, formatCurrency(productDetail.product?.selling_price))
            ),
            React.createElement('div', { className: 'p-3 bg-purple-50 rounded' },
              React.createElement('p', { className: 'text-purple-600' }, 'MRP'),
              React.createElement('p', { className: 'font-semibold' }, formatCurrency(productDetail.product?.mrp))
            ),
            React.createElement('div', { className: 'p-3 bg-amber-50 rounded' },
              React.createElement('p', { className: 'text-amber-600' }, 'GST'),
              React.createElement('p', { className: 'font-semibold' }, productDetail.product?.gst_percentage, '%')
            )
          ),
          
          // Recent Transactions
          React.createElement('div', null,
            React.createElement('p', { className: 'font-semibold mb-2' }, 'Recent Transactions'),
            React.createElement('div', { className: 'max-h-40 overflow-y-auto border rounded' },
              (productDetail.recent_transactions || []).slice(0, 10).map(function(txn, idx) {
                // A transaction is INWARD (stock-increasing) when:
                //   1) `signed_quantity` is positive (newer ledger rows carry a sign), or
                //   2) `transaction_type` starts with one of the inward families:
                //      - inward          (GRN receipts)
                //      - adjustment_in   (manual stock adjustments)
                //      - audit_in        (Stock Audit excess)
                // Anything else (`outward`, `adjustment_out`, `audit_out`) is OUTWARD.
                var t = txn.transaction_type || '';
                var signed = txn.signed_quantity;
                var isIn = (typeof signed === 'number')
                  ? signed > 0
                  : (t.startsWith('inward') || t.startsWith('adjustment_in') || t.startsWith('audit_in'));
                return React.createElement('div', {
                  key: idx,
                  className: 'flex justify-between p-2 border-b last:border-b-0 text-sm'
                },
                  React.createElement('div', null,
                    React.createElement('span', { className: isIn ? 'text-green-600' : 'text-red-600' },
                      isIn ? '+' : '-', txn.quantity
                    ),
                    React.createElement('span', { className: 'text-slate-600 ml-2' }, txn.reference_number)
                  ),
                  React.createElement('span', { className: 'text-slate-500' }, txn.timestamp?.slice(0, 10))
                );
              })
            )
          )
        )
      )
    ),
    
    // Bulk Upload Dialog
    React.createElement(Dialog, { open: bulkDialogOpen, onOpenChange: closeBulkDialog },
      React.createElement(DialogContent, { className: 'max-w-3xl max-h-[90vh] overflow-y-auto' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
            React.createElement(Upload, { className: 'w-5 h-5 text-purple-600' }),
            'Bulk Upload Inventory'
          )
        ),
        
        // Step Indicator
        React.createElement('div', { className: 'flex items-center justify-center gap-4 py-4 border-b' },
          [1, 2, 3, 4].map(function(s) {
            var labels = ['Upload', 'Preview', 'Confirm', 'Result'];
            return React.createElement('div', { key: s, className: 'flex items-center gap-2 ' + (bulkStep >= s ? 'text-purple-600' : 'text-gray-400') },
              React.createElement('div', { className: 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ' + (bulkStep >= s ? 'bg-purple-600 text-white' : 'bg-gray-200') }, s),
              React.createElement('span', { className: 'text-sm font-medium hidden sm:inline' }, labels[s-1])
            );
          })
        ),
        
        // Step 1: Upload File
        bulkStep === 1 && React.createElement('div', { className: 'py-6 space-y-4' },
          React.createElement('div', { className: 'text-center p-6 border-2 border-dashed rounded-lg bg-gray-50' },
            React.createElement(FileSpreadsheet, { className: 'w-12 h-12 mx-auto text-gray-400 mb-3' }),
            React.createElement('p', { className: 'font-medium mb-2' }, 'Upload Excel File'),
            React.createElement('p', { className: 'text-sm text-gray-500 mb-4' }, 'Upload your inventory update file (.xlsx or .csv)'),
            React.createElement('input', { 
              type: 'file', 
              accept: '.xlsx,.xls,.csv', 
              onChange: handleFileSelect,
              className: 'block mx-auto text-sm'
            }),
            bulkFile && React.createElement('p', { className: 'text-green-600 mt-2' }, 'Selected: ', bulkFile.name)
          ),
          React.createElement('div', { className: 'flex justify-between items-center' },
            React.createElement('div', { className: 'flex gap-2' },
              React.createElement(Button, { variant: 'outline', onClick: downloadTemplate, className: 'gap-2' },
                React.createElement(Download, { className: 'w-4 h-4' }),
                'Download Template'
              ),
              React.createElement(CsvDownloadButton, { onDownload: downloadTemplate, testId: 'inventory-template-csv', label: 'Template CSV', size: 'default' })
            ),
            React.createElement(Button, { onClick: validateUpload, disabled: !bulkFile || validating, className: 'gap-2 bg-purple-600 hover:bg-purple-700' },
              validating ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(CheckCircle2, { className: 'w-4 h-4' }),
              validating ? 'Validating...' : 'Validate & Preview'
            )
          )
        ),
        
        // Step 2: Preview
        bulkStep === 2 && React.createElement('div', { className: 'py-4 space-y-4' },
          // Summary
          React.createElement('div', { className: 'grid grid-cols-2 gap-4' },
            React.createElement(Card, { className: 'p-4 border-green-200 bg-green-50' },
              React.createElement('div', { className: 'flex items-center gap-2' },
                React.createElement(CheckCircle2, { className: 'w-5 h-5 text-green-600' }),
                React.createElement('span', { className: 'font-semibold text-green-700' }, 'Valid Rows: ', validRows.length)
              )
            ),
            React.createElement(Card, { className: 'p-4 border-red-200 bg-red-50' },
              React.createElement('div', { className: 'flex items-center gap-2' },
                React.createElement(XCircle, { className: 'w-5 h-5 text-red-600' }),
                React.createElement('span', { className: 'font-semibold text-red-700' }, 'Error Rows: ', errorRows.length)
              )
            )
          ),
          
          // Error Rows
          errorRows.length > 0 && React.createElement('div', null,
            React.createElement('div', { className: 'flex items-center justify-between mb-2' },
              React.createElement('p', { className: 'font-semibold text-red-700' }, 'Errors Found:'),
              React.createElement(Button, { 
                variant: 'outline', 
                size: 'sm', 
                onClick: function() { downloadErrorReport(errorRows); },
                className: 'gap-1 border-red-300 text-red-700 hover:bg-red-50',
                'data-testid': 'download-error-report-btn'
              },
                React.createElement(Download, { className: 'w-4 h-4' }),
                'Download Error Report'
              )
            ),
            React.createElement('div', { className: 'max-h-32 overflow-y-auto border border-red-200 rounded bg-red-50 p-2' },
              errorRows.map(function(err, idx) {
                return React.createElement('div', { key: idx, className: 'text-sm text-red-700 py-1' },
                  'Row ', err.row, ' (', err.sku, '): ', err.error
                );
              })
            )
          ),
          
          // Valid Rows Preview
          validRows.length > 0 && React.createElement('div', null,
            React.createElement('p', { className: 'font-semibold mb-2' }, 'Items to Update:'),
            React.createElement('div', { className: 'max-h-48 overflow-y-auto border rounded' },
              React.createElement('table', { className: 'w-full text-sm' },
                React.createElement('thead', { className: 'bg-gray-100 sticky top-0' },
                  React.createElement('tr', null,
                    React.createElement('th', { className: 'px-3 py-2 text-left' }, 'SKU'),
                    React.createElement('th', { className: 'px-3 py-2 text-left' }, 'Product'),
                    React.createElement('th', { className: 'px-3 py-2 text-center' }, 'Current'),
                    React.createElement('th', { className: 'px-3 py-2 text-center' }, 'New Qty'),
                    React.createElement('th', { className: 'px-3 py-2 text-left' }, 'Remarks')
                  )
                ),
                React.createElement('tbody', null,
                  validRows.slice(0, 20).map(function(row, idx) {
                    return React.createElement('tr', { key: idx, className: 'border-b' },
                      React.createElement('td', { className: 'px-3 py-2 font-mono' }, row.sku),
                      React.createElement('td', { className: 'px-3 py-2' }, row.product_name),
                      React.createElement('td', { className: 'px-3 py-2 text-center' }, row.current_stock),
                      React.createElement('td', { className: 'px-3 py-2 text-center font-semibold text-purple-600' }, row.new_quantity),
                      React.createElement('td', { className: 'px-3 py-2 text-gray-500' }, row.remarks || '-')
                    );
                  })
                )
              ),
              validRows.length > 20 && React.createElement('p', { className: 'text-center text-sm text-gray-500 py-2' }, '...and ', validRows.length - 20, ' more items')
            )
          ),
          
          React.createElement('div', { className: 'flex justify-between pt-4' },
            React.createElement(Button, { variant: 'outline', onClick: function() { setBulkStep(1); } }, 'Back'),
            React.createElement(Button, { onClick: function() { setBulkStep(3); }, disabled: validRows.length === 0, className: 'bg-purple-600 hover:bg-purple-700' }, 'Continue')
          )
        ),
        
        // Step 3: Confirm
        bulkStep === 3 && React.createElement('div', { className: 'py-4 space-y-4' },
          React.createElement(Card, { className: 'p-4 bg-amber-50 border-amber-200' },
            React.createElement('div', { className: 'flex items-start gap-2' },
              React.createElement(AlertTriangle, { className: 'w-5 h-5 text-amber-600 mt-0.5' }),
              React.createElement('div', null,
                React.createElement('p', { className: 'font-semibold text-amber-700' }, 'Confirm Stock Update'),
                React.createElement('p', { className: 'text-sm text-amber-600' }, 'You are about to update ', validRows.length, ' products. This action will be logged.')
              )
            )
          ),
          
          // Update Mode Selection
          React.createElement('div', null,
            React.createElement(Label, { className: 'font-semibold' }, 'Update Mode *'),
            React.createElement('div', { className: 'grid grid-cols-2 gap-4 mt-2' },
              React.createElement('div', { 
                onClick: function() { setUpdateMode('add'); },
                className: 'p-4 border-2 rounded-lg cursor-pointer transition-colors ' + (updateMode === 'add' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300')
              },
                React.createElement('p', { className: 'font-semibold' }, 'Add Stock'),
                React.createElement('p', { className: 'text-sm text-gray-500' }, 'New Qty = Current + Uploaded Qty')
              ),
              React.createElement('div', { 
                onClick: function() { setUpdateMode('replace'); },
                className: 'p-4 border-2 rounded-lg cursor-pointer transition-colors ' + (updateMode === 'replace' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300')
              },
                React.createElement('p', { className: 'font-semibold' }, 'Replace Stock'),
                React.createElement('p', { className: 'text-sm text-gray-500' }, 'Existing Qty will be overwritten')
              )
            )
          ),
          
          // Adjustment Reason
          React.createElement('div', null,
            React.createElement(Label, { className: 'font-semibold' }, 'Adjustment Reason *'),
            React.createElement(Textarea, { 
              value: adjustmentReason,
              onChange: function(e) { setAdjustmentReason(e.target.value); },
              placeholder: 'e.g., Quarterly stock count correction, New stock arrival, etc.',
              className: 'mt-2'
            })
          ),
          
          React.createElement('div', { className: 'flex justify-between pt-4' },
            React.createElement(Button, { variant: 'outline', onClick: function() { setBulkStep(2); } }, 'Back'),
            React.createElement(Button, { 
              onClick: executeBulkUpload, 
              disabled: submittingBulk || !adjustmentReason.trim(), 
              className: 'gap-2 bg-purple-600 hover:bg-purple-700' 
            },
              submittingBulk ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(CheckCircle2, { className: 'w-4 h-4' }),
              submittingBulk ? 'Processing...' : 'Execute Update'
            )
          )
        ),
        
        // Step 4: Result
        bulkStep === 4 && bulkResult && React.createElement('div', { className: 'py-4 space-y-4 text-center' },
          React.createElement(CheckCircle2, { className: 'w-16 h-16 mx-auto text-green-500' }),
          React.createElement('h3', { className: 'text-xl font-bold' }, 'Bulk Upload Complete!'),
          React.createElement('p', { className: 'text-gray-600' }, bulkResult.message),
          
          React.createElement('div', { className: 'grid grid-cols-2 gap-4 max-w-xs mx-auto' },
            React.createElement(Card, { className: 'p-4 bg-green-50' },
              React.createElement('p', { className: 'text-2xl font-bold text-green-600' }, bulkResult.success_count),
              React.createElement('p', { className: 'text-sm text-green-700' }, 'Updated')
            ),
            React.createElement(Card, { className: 'p-4 bg-red-50' },
              React.createElement('p', { className: 'text-2xl font-bold text-red-600' }, bulkResult.failed_count),
              React.createElement('p', { className: 'text-sm text-red-700' }, 'Failed')
            )
          ),
          
          React.createElement(Button, { onClick: closeBulkDialog, className: 'mt-4' }, 'Close')
        )
      )
    ),
    
    // Upload History Dialog
    React.createElement(Dialog, { open: historyDialogOpen, onOpenChange: setHistoryDialogOpen },
      React.createElement(DialogContent, { className: 'max-w-2xl max-h-[80vh] overflow-y-auto' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
            React.createElement(History, { className: 'w-5 h-5 text-purple-600' }),
            'Inventory Upload History'
          )
        ),
        React.createElement('div', { className: 'py-4' },
          uploadHistory.length === 0 
            ? React.createElement('p', { className: 'text-center text-gray-500 py-8' }, 'No upload history found')
            : React.createElement('div', { className: 'space-y-3' },
                uploadHistory.map(function(upload) {
                  return React.createElement(Card, { key: upload.id, className: 'p-4' },
                    React.createElement('div', { className: 'flex items-start justify-between' },
                      React.createElement('div', { className: 'flex-1' },
                        React.createElement('p', { className: 'font-semibold' }, upload.uploaded_by_name),
                        React.createElement('p', { className: 'text-sm text-gray-500' }, upload.upload_date?.slice(0, 16).replace('T', ' ')),
                        React.createElement('p', { className: 'text-sm' }, 
                          'Mode: ', React.createElement('span', { className: 'font-medium' }, upload.update_mode?.toUpperCase()),
                          ' | Reason: ', upload.adjustment_reason
                        )
                      ),
                      React.createElement('div', { className: 'text-right' },
                        React.createElement(Badge, { className: upload.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800' }, upload.status),
                        React.createElement('p', { className: 'text-sm mt-1' },
                          React.createElement('span', { className: 'text-green-600 font-medium' }, upload.success_count, ' success'),
                          ' / ',
                          React.createElement('span', { className: 'text-red-600 font-medium' }, upload.failed_count, ' failed')
                        ),
                        upload.failed_count > 0 && React.createElement(Button, {
                          variant: 'outline',
                          size: 'sm',
                          onClick: function() { downloadHistoryErrorReport(upload.id); },
                          className: 'mt-2 gap-1 border-red-300 text-red-700 hover:bg-red-50 text-xs',
                          'data-testid': 'download-history-error-btn'
                        },
                          React.createElement(Download, { className: 'w-3 h-3' }),
                          'Download Errors'
                        )
                      )
                    )
                  );
                })
              )
        )
      )
    ),

    // SKU Order History Dialog (lazy-loads on open)
    React.createElement(SkuOrderHistoryDialog, {
      open: historyOpen,
      onOpenChange: setHistoryOpen,
      product: historyProduct
    })
  );
}

export default InventoryStockList;
